import { useState, useEffect, useLayoutEffect, useCallback } from 'react';

/**
 * useTheme - Centralized dark mode management hook
 *
 * Features:
 * - Initializes from localStorage or system preference (no flash)
 * - Listens for system theme changes
 * - Safe localStorage access (handles private browsing)
 * - Syncs theme to document.documentElement
 */

// Helper to safely access localStorage
const getStoredTheme = () => {
  try {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme');
    }
  } catch {
    // localStorage may throw in private browsing mode
  }
  return null;
};

const setStoredTheme = (theme) => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
  } catch {
    // localStorage may throw in private browsing mode
  }
};

// Get initial theme to prevent flash
const getInitialTheme = () => {
  const stored = getStoredTheme();
  if (stored) {
    return stored === 'dark';
  }
  // Check system preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

export const useTheme = () => {
  // Initialize with stored/system preference to prevent flash
  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);

  // Sync theme to document - use useLayoutEffect to prevent flash
  useLayoutEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    setStoredTheme(isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only update if user hasn't explicitly set a preference
      const stored = getStoredTheme();
      if (!stored) {
        setIsDarkMode(e.matches);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  return {
    isDarkMode,
    setIsDarkMode,
    toggleTheme
  };
};

export default useTheme;
